import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Card, CardContent, CardActions, Button, Grid } from '@mui/material';
import handicapperPicksService from '../../services/handicapperPicksService';
import { useAuth } from '../../contexts/AuthContext';
import EditPickDialog from './EditPickDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { sportKeyToShort } from '../../utils/sportsMapping';

const MyPicksPanel = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState([]);
  const [stats, setStats] = useState({});
  const [editingPick, setEditingPick] = useState(null);
  const [deletingPick, setDeletingPick] = useState(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const doc = await handicapperPicksService.getPicksForHandicapperV2(currentUser.uid);
      setPicks(doc.picks || []);
      setStats({ totalPicks: doc.totalPicks, wins: doc.wins, losses: doc.losses, pushes: doc.pushes });
    } catch (err) {
      console.error('Failed to load picks', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleOpenEdit = (pick) => setEditingPick(pick);
  const handleCloseEdit = () => setEditingPick(null);

  const handleSaveEdit = async (pickId, updates) => {
    try {
      await handicapperPicksService.updatePickV2(currentUser.uid, pickId, updates);
      await load();
      handleCloseEdit();
    } catch (err) {
      console.error('Failed to save pick', err);
      throw err;
    }
  };

  const handleDelete = (pick) => setDeletingPick(pick);
  const handleConfirmDelete = async () => {
    if (!deletingPick) return;
    try {
      await handicapperPicksService.deletePickV2(currentUser.uid, deletingPick.id);
      setDeletingPick(null);
      await load();
    } catch (err) {
      console.error('Failed to delete pick', err);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;

  if (!picks || picks.length === 0) return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">You have no picks yet.</Typography>
      <Typography variant="body2" color="text.secondary">Create picks from the odds list to see them here.</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>My Picks</Typography>
      <Grid container spacing={2}>
        {picks.map((pick) => (
          <Grid item xs={12} md={6} key={pick.id}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">{sportKeyToShort(pick.sport) || 'N/A'}</Typography>
                <Typography variant="h6">{pick.awayTeam} @ {pick.homeTeam}</Typography>
                <Typography variant="body1" sx={{ mt: 1 }}><strong>Pick:</strong> {pick.pickedTeam} ({pick.pickType})</Typography>
                <Typography variant="body2"><strong>Odds:</strong> {typeof pick.odds === 'number' ? pick.odds : '—'}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{pick.analysis}</Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => handleOpenEdit(pick)}>Edit</Button>
                <Button size="small" color="error" onClick={() => handleDelete(pick)}>Delete</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {editingPick && (
        <EditPickDialog
          open={!!editingPick}
          pick={editingPick}
          onClose={handleCloseEdit}
          onSave={handleSaveEdit}
        />
      )}

      {deletingPick && (
        <ConfirmDeleteDialog
          open={!!deletingPick}
          onClose={() => setDeletingPick(null)}
          onConfirm={handleConfirmDelete}
          pick={deletingPick}
        />
      )}
    </Box>
  );
};

export default MyPicksPanel;
